import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close language menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('[data-lang-menu]')) {
        setIsLangMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    setIsLangMenuOpen(false);
  };

  const getCurrentLanguage = () => {
    const lang = i18n.language;
    if (lang?.startsWith('en')) return 'EN';
    if (lang?.startsWith('lt')) return 'LT';
    if (lang?.startsWith('ru')) return 'RU';
    if (lang?.startsWith('pl')) return 'PL';
    return 'EN';
  };

  const languages = [
    { code: 'en', label: 'English', short: 'EN' },
    { code: 'lt', label: 'Lietuvių', short: 'LT' },
    { code: 'ru', label: 'Русский', short: 'RU' },
    { code: 'pl', label: 'Polski', short: 'PL' }
  ];

  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.cases'), path: '/properties' },
    { name: t('nav.about'), path: '/about-us' },
    { name: t('nav.faq'), path: '/faq' }
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#102131]/95 backdrop-blur-md shadow-lg' : 'bg-[#102131]/95 backdrop-blur-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 transition-transform hover:scale-105">
            <img
              src="https://customer-assets.emergentagent.com/job_luxe-estates-6/artifacts/94kq3auf_FundoraLogotipasSviesiVersija.png"
              alt="Fundora Logo"
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors duration-200 ${
                  location.pathname === link.path
                    ? 'text-[#CDA259]'
                    : 'text-[#F7FDFF] hover:text-[#CDA259]'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Language Switcher Dropdown */}
            <div className="relative" data-lang-menu>
              <button
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className="flex items-center space-x-1.5 text-sm font-medium text-[#F7FDFF] hover:text-[#CDA259] transition-colors duration-200"
                data-testid="language-switcher"
              >
                <Globe size={18} />
                <span>{getCurrentLanguage()}</span>
                <ChevronDown size={14} className={`transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isLangMenuOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-[#102131] border border-[#CDA259]/30 rounded-lg shadow-xl overflow-hidden">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center justify-between ${
                        getCurrentLanguage() === lang.short
                          ? 'bg-[#CDA259]/20 text-[#CDA259]'
                          : 'text-[#F7FDFF] hover:bg-[#CDA259]/10 hover:text-[#CDA259]'
                      }`}
                      data-testid={`lang-${lang.code}`}
                    >
                      <span>{lang.label}</span>
                      <span className="text-xs opacity-70">{lang.short}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <Link to="/contact">
              <Button className="bg-[#CDA259] text-[#102131] hover:bg-[#CDA259]/90 font-semibold transition-all duration-200 hover:scale-105">
                {t('nav.bookConsultation')}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-4">
            {/* Mobile Language Switcher */}
            <div className="relative" data-lang-menu>
              <button
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className="flex items-center space-x-1 text-sm font-medium text-[#F7FDFF] hover:text-[#CDA259] transition-colors"
                data-testid="mobile-language-switcher"
              >
                <Globe size={18} />
                <span>{getCurrentLanguage()}</span>
                <ChevronDown size={14} className={`transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isLangMenuOpen && (
                <div className="absolute right-0 mt-2 w-36 bg-[#102131] border border-[#CDA259]/30 rounded-lg shadow-xl overflow-hidden z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors ${
                        getCurrentLanguage() === lang.short
                          ? 'bg-[#CDA259]/20 text-[#CDA259]'
                          : 'text-[#F7FDFF] hover:bg-[#CDA259]/10 hover:text-[#CDA259]'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <button
              className="text-[#F7FDFF] hover:text-[#CDA259] transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-[#102131]/98 backdrop-blur-lg border-t border-[#CDA259]/20">
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block text-base font-medium transition-colors py-2 ${
                  location.pathname === link.path
                    ? 'text-[#CDA259]'
                    : 'text-[#F7FDFF] hover:text-[#CDA259]'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
              <Button className="w-full bg-[#CDA259] text-[#102131] hover:bg-[#CDA259]/90 font-semibold">
                {t('nav.bookConsultation')}
              </Button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
